/**
 * Frozen-shape payload structs for every canonical event. ARCHITECTURE.md §6.2.
 *
 * Every field is always present with a numeric/string/boolean default, so call sites stay
 * monomorphic and listeners never branch on `undefined`. Emitters allocate ONE payload per
 * event type at init and mutate it in place.
 *
 * `tests/unit/events.test.js` asserts that the table here matches ARCHITECTURE.md §6.2.
 */

/** @type {Record<string, () => object>} */
export const PAYLOAD_FACTORIES = {
  'combat:move-start': () => ({
    tick: 0, fighter: 0, moveId: '', moveName: '', startup: 0, active: 0, recovery: 0,
    cancelWindowStart: 0, level: '', isThrow: false, isSpecial: false,
  }),
  'combat:active': () => ({ tick: 0, fighter: 0, moveId: '', hitboxIndex: 0, framesActive: 0 }),
  'combat:whiff': () => ({ tick: 0, fighter: 0, moveId: '', recoveryFrames: 0 }),
  'combat:hit': () => ({
    tick: 0, attacker: 0, defender: 0, moveId: '', moveName: '', damage: 0, rawDamage: 0,
    scaling: 1, comboHits: 0, comboDamage: 0, level: '', hitstun: 0, hitstop: 0,
    x: 0, y: 0, z: 0, nx: 0, ny: 0, nz: 0,
    launcher: false, counter: false, punish: false, airborne: false, surface: 'body',
  }),
  'combat:block': () => ({
    tick: 0, attacker: 0, defender: 0, moveId: '', blockstun: 0, chipDamage: 0, pushback: 0,
    crouching: false, x: 0, y: 0, z: 0, guardBreak: false, guardMeter: 1,
  }),
  'combat:counter': () => ({ tick: 0, attacker: 0, defender: 0, moveId: '', bonusDamage: 0, hitstunBonus: 0 }),
  'combat:parry': () => ({ tick: 0, defender: 0, attacker: 0, moveId: '', window: 0, frameAdvantage: 0 }),
  'combat:armor': () => ({ tick: 0, fighter: 0, moveId: '', absorbedDamage: 0, hitsRemaining: 0 }),
  'combat:throw': () => ({
    tick: 0, attacker: 0, defender: 0, moveId: '', moveName: '', damage: 0, breakWindow: 0, chainIndex: 0,
  }),
  'combat:throw-break': () => ({ tick: 0, attacker: 0, defender: 0, moveId: '', breakTick: 0 }),
  'combat:launch': () => ({ tick: 0, attacker: 0, defender: 0, moveId: '', launchVy: 0, comboHits: 0 }),
  'combat:wall-splat': () => ({ tick: 0, fighter: 0, wallSide: 0, impactSpeed: 0, x: 0, y: 0, z: 0, stunTicks: 0 }),
  'combat:wall-bounce': () => ({ tick: 0, fighter: 0, wallSide: 0, impactSpeed: 0, x: 0, y: 0, z: 0 }),
  'combat:knockdown': () => ({
    tick: 0, fighter: 0, hard: false, faceUp: false, headTowardOpponent: false, x: 0, y: 0, z: 0,
  }),
  'combat:wakeup': () => ({ tick: 0, fighter: 0, option: '', invulnTicks: 0 }),
  'combat:combo-end': () => ({ tick: 0, attacker: 0, defender: 0, hits: 0, damage: 0, maxScaling: 1, wallUsed: false }),
  'combat:finisher': () => ({ tick: 0, attacker: 0, defender: 0, moveId: '', phase: '' }),
  'combat:round-start': () => ({ tick: 0, round: 1, bestOf: 3, timerTicks: 0, storm: 0 }),
  'combat:round-end': () => ({
    tick: 0, round: 1, winner: -1, reason: '', perfect: false, timeRemaining: 0, p0Health: 0, p1Health: 0,
  }),
  'combat:match-end': () => ({ tick: 0, winner: -1, rounds0: 0, rounds1: 0, perfectRounds: 0 }),

  'fighter:state': () => ({ tick: 0, fighter: 0, from: '', to: '', moveId: '' }),
  'fighter:damage': () => ({ tick: 0, fighter: 0, amount: 0, health: 0, maxHealth: 0, source: '', fatal: false }),
  'fighter:defeat': () => ({ tick: 0, fighter: 0, cause: '' }),
  'fighter:step': () => ({ tick: 0, fighter: 0, foot: 0, x: 0, y: 0, z: 0, surface: 'concrete', speed: 0 }),

  'input:command': () => ({ tick: 0, player: 0, command: '', bufferedFrames: 0, direction: 0 }),

  'audio:cue': () => ({ tick: 0, cue: '', x: 0, y: 0, z: 0, gain: 1, pitch: 1, variant: 0, surface: '' }),
  'fx:impact': () => ({
    tick: 0, kind: '', x: 0, y: 0, z: 0, nx: 0, ny: 0, nz: 0, strength: 1, hue: 0, surface: '', scale: 1,
  }),
  'camera:impulse': () => ({
    tick: 0, kind: '', strength: 0, dirX: 0, dirY: 0, dirZ: 0, durationTicks: 0, shakeHz: 0, zoom: 0,
  }),
  'camera:cinematic': () => ({ tick: 0, shot: '', subject: 0, target: 0, durationTicks: 0, phase: '' }),

  'world:prop-hit': () => ({ tick: 0, propId: '', impulse: 0, x: 0, y: 0, z: 0, destroy: false }),
  'ui:navigate': () => ({ tick: 0, screen: '', from: '' }),
  'engine:quality': () => ({ tick: 0, preset: '', reason: '', renderScale: 1 }),
};

export const CANONICAL_EVENTS = Object.freeze(Object.keys(PAYLOAD_FACTORIES));

/** Creates a fresh payload for an event. Call at init, never per-frame. */
export function createPayload(name) {
  const factory = PAYLOAD_FACTORIES[name];
  if (!factory) throw new Error(`Unknown event "${name}". Add it to ARCHITECTURE.md §6.2 and event-payloads.js.`);
  return factory();
}

/**
 * Creates a payload bank: `{ 'combat:hit': {...}, ... }` for the events a system emits.
 * Emitters keep one bank and mutate its members.
 */
export function createPayloadBank(names) {
  const bank = Object.create(null);
  for (const name of names) bank[name] = createPayload(name);
  return bank;
}

/**
 * Resets a payload to its declared defaults without allocating. Emitters should call this
 * before filling a payload so a stale field from a previous emit can never leak.
 */
export function resetPayload(name, obj) {
  const defaults = DEFAULTS[name];
  for (const k in defaults) obj[k] = defaults[k];
  return obj;
}

const DEFAULTS = Object.create(null);
for (const name of CANONICAL_EVENTS) DEFAULTS[name] = PAYLOAD_FACTORIES[name]();
